import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import SEO from "@/components/feature/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { articleDetail } from "@/mocks/siteData";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const formatViews = (n: number) => {
    if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
    return n.toString();
  };

  const article = articleDetail;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "author": {
      "@type": "Person",
      "name": article.author,
    },
    "datePublished": article.date,
    "image": article.image,
    "publisher": {
      "@type": "Organization",
      "name": "911爆料网",
      "url": "https://911baoliao.com",
    },
    "description": article.title,
    "inLanguage": "zh-CN",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://911baoliao.com/article/${id}`,
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": article.faq.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer,
      },
    })),
  };

  return (
    <>
      <SEO
        title={`${article.title} - 911爆料网`}
        description={`911爆料网独家报道：${article.title}。${article.author}出品，发布于${article.date}。`}
        keywords={`911爆料网,${article.category},爆料,吃瓜,黑料`}
        canonicalUrl={`https://911baoliao.com/article/${id}`}
        ogTitle={article.title}
        ogDescription={`911爆料网独家报道：${article.title}`}
        ogImage={article.image}
        ogType="article"
        schema={{ "@graph": [articleSchema, faqSchema] }}
      />
      <Navbar />
      <main>
        <article>
          <section className="relative w-full min-h-[60vh] md:min-h-[70vh] flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
                title={`911爆料 - ${article.title}`}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background-50"></div>
            </div>

            <div className="relative z-10 w-full flex flex-col items-center text-center px-4 pt-20 pb-12">
              <div className="max-w-3xl mx-auto">
                <div className="px-4 md:px-8">
                  <Breadcrumb
                    items={[
                      { label: "首页", path: "/" },
                      { label: article.category, path: `/category/wanghong-heiliao` },
                      { label: article.title },
                    ]}
                  />
                </div>

                <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading text-foreground-50 leading-tight mt-4 mb-6">
                  {article.title}
                </h1>

                <div className="flex items-center justify-center gap-4 md:gap-6 flex-wrap text-xs md:text-sm text-foreground-400 mb-8">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    {article.author}
                  </span>
                  <span className="text-foreground-600">|</span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    {article.date}
                  </span>
                  <span className="text-foreground-600">|</span>
                  <span className="flex items-center gap-1.5 text-accent-400 font-medium">
                    <i className="ri-shield-check-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    {article.source}
                  </span>
                </div>

                <button
                  className="inline-flex items-center gap-2 px-6 py-2.5 border border-foreground-300/30 hover:bg-foreground-50/10 text-foreground-300 text-sm font-medium rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
                  aria-label="收藏本瓜"
                >
                  <i className="ri-bookmark-line w-4 h-4 flex items-center justify-center"></i>
                  收藏本瓜
                </button>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-8 py-10 md:py-16">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-3 mb-8 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                  {formatViews(article.views)}次阅读
                </span>
                <span>·</span>
                <span>{article.category}</span>
              </div>

              <div
                className="prose-custom text-foreground-200 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>
          </section>

          <section className="px-4 md:px-8 py-10 md:py-16 bg-background-50">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-6">
                    <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                    <span className="text-xs text-foreground-500 font-medium tracking-wider">爆料者说</span>
                  </div>

                  {article.testimonials.map((testimonial, index) => (
                    <div key={index} className={index > 0 ? "mt-10" : ""}>
                      <blockquote className="text-xl md:text-2xl font-heading text-foreground-100 leading-relaxed mb-4">
                        &ldquo;{testimonial.quote}&rdquo;
                      </blockquote>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground-300">{testimonial.name}</span>
                        {testimonial.verified && (
                          <span className="flex items-center gap-1 text-xs text-accent-400">
                            <i className="ri-verified-badge-fill w-3.5 h-3.5 flex items-center justify-center"></i>
                            认证爆料
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="relative rounded-2xl overflow-hidden aspect-[3/4]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Black%20and%20white%20portrait%20of%20a%20person%20in%20profile%20looking%20slightly%20upward%2C%20shoulders%20and%20above%20only%2C%20dramatic%20side%20lighting%20on%20face%2C%20pure%20white%20background%2C%20anonymous%20whistleblower%20aesthetic%2C%20cinematic%20editorial%20style%2C%20film%20grain%20texture%2C%20moody%20and%20mysterious&width=800&height=1067&seq=article-testimonial-portrait-01&orientation=portrait"
                    alt="爆料者"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-8 py-10 md:py-16">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="relative rounded-2xl overflow-hidden aspect-[16/10]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Dark%20newspaper%20collage%20with%20multiple%20headlines%20and%20images%20overlapping%2C%20investigative%20journalism%20theme%2C%20dramatic%20red%20accent%20highlights%2C%20deep%20shadows%2C%20cinematic%20composition%2C%20moody%20atmosphere&width=1600&height=1000&seq=article-related-section-01&orientation=landscape"
                    alt="相关爆料"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background-100/80 to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <span className="px-2.5 py-1 rounded-full bg-background-100/60 backdrop-blur-sm text-foreground-400 text-xs whitespace-nowrap">
                      深度追踪
                    </span>
                  </div>
                  <div className="absolute bottom-6 left-6">
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-50 leading-tight">
                      更多大瓜
                    </h2>
                  </div>
                </div>

                <div className="flex flex-col justify-center items-center text-center px-4">
                  <h2 className="text-2xl md:text-3xl font-heading text-foreground-50 mb-4">
                    相关爆料
                  </h2>
                  <p className="text-sm text-foreground-500 leading-relaxed max-w-md mb-8">
                    看了这篇还不过瘾？以下是小编为您精选的相关爆料文章，继续吃瓜不要停！
                  </p>
                  <div className="space-y-3 w-full max-w-md">
                    {article.relatedArticles.map((related) => (
                      <Link
                        key={related.id}
                        to={`/article/${related.id}`}
                        className="block w-full px-4 py-3 text-sm text-foreground-300 hover:text-foreground-100 bg-background-100 hover:bg-background-200 rounded-lg transition-all duration-200 cursor-pointer text-left border border-background-300/30"
                      >
                        {related.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-8 py-10 md:py-16 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-50 mb-8">
                常见问题
              </h2>
              <div className="space-y-4">
                {article.faq.map((item, index) => (
                  <details key={index} className="group border border-background-300/30 rounded-xl overflow-hidden">
                    <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-foreground-200 hover:text-foreground-100 transition-colors">
                      <span className="text-sm font-medium pr-4">{item.question}</span>
                      <i className="ri-arrow-down-s-line w-5 h-5 flex items-center justify-center group-open:rotate-180 transition-transform text-foreground-500"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-500 leading-relaxed">{item.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}